import { useCallback, useState } from "react";
import { Button, Empty, Form, Input, List } from "antd";
import { useComments } from "../../../../hooks/useTaskComments";
import { StyledComments } from "./styles";
import CommentItem from "./CommentItem";
import {
  addEditComment,
  updateCommentReaction,
} from "../../../../store/tasks/thunks";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../../../store/user/selectors";
import ReactionsModal from "../../../../components/shared/Reactions/ReactionsModal";
import { setKeyValue } from "../../../../store/tasks/slice";
import { selectTasksReactions } from "../../../../store/tasks/selectors";
import { MentionInput } from "../../../../components/shared/Mentions";
import {
  convertIdsToMentions,
  convertMentionsToIds,
} from "../../../../components/shared/Mentions/helper";

const Comments = ({ selectedTask, tenant_id, users }) => {
  const { loading, comments } = useComments(selectedTask, tenant_id);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const user = useSelector(selectUser);
  const reactions = useSelector(selectTasksReactions);

  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const handleFinish = useCallback(
    async (values) => {
      try {
        const payload = {
          task_id: selectedTask?.id,
          column_id: selectedTask?.column_id,
          id: values?.id || (values?.isEdit ? editingId : undefined),
          emoji: values?.emoji || undefined,
          tenant_id,
          user_id: user?.user_id,
        };

        if (values?.emoji) {
          await dispatch(updateCommentReaction(payload)).unwrap();
          return;
        }

        setIsSubmitting(true);

        payload.content = convertMentionsToIds(values?.content, users);

        await dispatch(addEditComment(payload)).unwrap();

        if (values?.isEdit) {
          setEditingId(null);
          setEditValue("");
        } else {
          form.resetFields();
        }
      } catch (error) {
        console.log(":rocket: ~ index.jsx:30 ~ Comments ~ error:", error);
      } finally {
        if (!values?.emoji) {
          setIsSubmitting(false);
        }
      }
    },
    [dispatch, selectedTask, editingId, tenant_id, form, user],
  );

  const handleEdit = useCallback(
    (comment) => {
      setEditingId(comment.id);
      setEditValue(convertIdsToMentions(comment?.content, users));
    },
    [users],
  );

  const handleEditCancel = useCallback(() => {
    setEditingId(null);
    setEditValue("");
  }, []);

  return (
    <StyledComments>
      <h3 className="comment-title">Comments</h3>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        className="comment-new"
      >
        <Form.Item
          name="content"
          rules={[
            {
              required: true,
              whitespace: true,
              message: "Please enter a comment.",
            },
          ]}
          style={{ marginBottom: 0 }}
        >
          <MentionInput
            autoSize={{ minRows: 2, maxRows: 5 }}
            disabled={isSubmitting}
            className="comment-new-input"
            users={users}
          />
        </Form.Item>

        <Form.Item className="comment-submit-btn">
          <Button
            htmlType="submit"
            type="primary"
            loading={isSubmitting && !editingId}
            className="comment-submit-btn"
            size="small"
          >
            Save
          </Button>
        </Form.Item>
      </Form>

      <List
        className="comments-list"
        dataSource={comments}
        locale={{ emptyText: <Empty description="No comments yet" /> }}
        loading={loading}
        renderItem={(comment) => {
          return (
            <CommentItem
              comment={comment}
              editingId={editingId}
              editValue={editValue}
              setEditValue={setEditValue}
              handleEdit={handleEdit}
              handleEditSave={handleFinish}
              handleEditCancel={handleEditCancel}
              loggedUser={user}
              isSubmitting={isSubmitting}
              users={users}
            />
          );
        }}
      />
      {reactions && (
        <ReactionsModal
          onCancel={() =>
            dispatch(
              setKeyValue({
                key: "reactions",
                value: null,
              }),
            )
          }
          users={users}
          reactions={reactions}
        />
      )}
    </StyledComments>
  );
};

export default Comments;
